import { H1, H2 } from 'components/atoms/Text';
import strapi, { getAllPages, getBySlug } from 'utils/strapi';

import { Box } from 'atomic-layout';
import Link from 'next/link';
import React from 'react';
import SEO from 'components/seo';
import dynamic from 'next/dynamic';
import format from 'date-fns/format';
import { getFooterProps } from 'components/layout/footer';
import { getHeaderProps } from 'components/layout/header';
import { getLoaderProps } from 'components/loader';
import readingTime from 'reading-time';
import styled from 'styled-components';

const EmailSignupComponent = dynamic(() =>
  import('components/content/email-signup')
);
const BlogList = dynamic(() => import('components/content/blog-list'));
const DynamicZone = dynamic(() => import('components/dynamic-zone'));
const InlineShareButtons = dynamic(async () => (await import('sharethis-reactjs')).InlineShareButtons)

const BlogContentWrapper = styled(Box)`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
`;

const LatestBlogsWrapper = styled(Box)`
  h2 {
    text-align: center;
  }
  
  @media screen and (max-width: 1000px) {
    :nth-child(2) {
      > div {
        padding: 0 2vw;
      }
      > div > div {
        width: 100%;
        min-width: unset;
      }
    } 
  }
`

const AuthorLink = styled.a`
  font-weight: bold;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.text};
`;

export default function BlogPost({
  slug,
  seoData,
  title,
  publishedAt,
  author,
  blocks,
  blogs,
  emailSignupBox,
}) {
  const { text: readTimeText } = readingTime(
    blocks
      .filter((block) => block.text)
      .map((block) => block.text)
      .join('\n')
  );

  return (
    <Box>
      <SEO {...seoData} />
      <BlogContentWrapper
        itemScope
        itemType="https://schema.org/Article"
        maxWidth="calc(100vw - 2rem)"
        maxWidthMd="800px"
      >
        <div>
          <H1 $textAlign="left" itemProp="headline">
            {title}
          </H1>
        </div>
        <div style={{ marginBottom: '5rem' }}>
          {author && (
            <>
              By{' '}
              <Link href={`/author/${author.slug}`} passHref legacyBehavior>
                <AuthorLink>{author.name}</AuthorLink>
              </Link>{' '}
              on{' '}
            </>
          )}
          <span itemProp="datePublished">
            {format(
              publishedAt ? Date.parse(publishedAt) : new Date(),
              'MMMM do, yyyy'
            )}
          </span>
          {' • '}
          <span>{readTimeText}</span>
        </div>
        <Box>
          <DynamicZone blocks={blocks || []} />
        </Box>
        <div
          style={{
            marginTop: '6rem',
            maxWidth: '650px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <EmailSignupComponent isFullWidth {...emailSignupBox} />
        </div>
        <div
          style={{
            borderTop: '1px solid white',
            borderBottom: '1px solid white',
            margin: '6rem 0',
            padding: '4rem 0',
          }}
        >
          <InlineShareButtons
            config={{
              alignment: 'justified',
              radius: 4,
              padding: 12,
              networks: [
                'facebook',
                'twitter',
                'sharethis',
                'email',
                'reddit',
              ],
              color: 'social',
              enabled: true,
              labels: 'cta',
              language: 'en',
            }}
          />
        </div>
      </BlogContentWrapper>
      {blogs.length >= 0 && (
          <LatestBlogsWrapper>
            <H2>Latest Blogs</H2>
            <BlogList blogs={blogs} />
          </LatestBlogsWrapper>
      )}
    </Box>
  );
}

export async function getStaticProps(ctx) {
  const { params } = ctx;
  const result = await getBySlug('/api/blog-posts', params.slug, {
    populate: {
      seo: {
        populate: '*',
        openGraphImage: {
          populate: '*',
        },
      },
      author: {
        populate: '*',
      },
      blocks: {
        populate: '*',
      },
    },
  });
  if (!result)
    return {
      notFound: true,
    };
  const { seo, title, publishedAt, author, blocks } = result.attributes;
  const { data: blogs } = await strapi('/api/blog-posts', {
    filters: {
      slug: {
        $ne: params.slug,
      },
    },
    pagination: { limit: 3 },
    populate: ['thumbnailImage'],
    sort: ['publishedAt:desc'],
  });
  const {
    data: { attributes: emailSignupBox },
  } = await strapi('/api/email-signup-box', {});

  return {
    props: {
      slug: params.slug,
      seoData: seo,
      title,
      publishedAt,
      author: author?.data?.attributes || null,
      blocks,
      blogs,
      footerProps: await getFooterProps(),
      headerProps: await getHeaderProps(),
      loaderProps: await getLoaderProps(),
      emailSignupBox: emailSignupBox || {},
    },
  };
}

export async function getStaticPaths() {
  const posts = await getAllPages('/api/blog-posts');
  return {
    paths: posts.map((post) => ({ params: { slug: post.attributes.slug } })),
    fallback: 'blocking',
  };
}
